import {
  ArrowDownUp,
  BadgeDollarSign,
  BarChart3,
  Calculator,
  Check,
  ChevronDown,
  ExternalLink,
  Heart,
  Info,
  Languages,
  Moon,
  RotateCw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Sun,
  X,
} from "lucide-react";
import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CALCULATOR_PATH, GUIDE_PATH, localizedPath, modelPath, parseRoute, providerPath } from "../shared/routes";
import { rankingsCopy } from "../shared/rankings";
import { fallbackCatalog } from "./fallbackCatalog";
import { detectLanguage, directionFor, languages, languageNames, localeFor, saveLanguage, translate, type Language } from "./i18n";
import { HomeDiscovery, SubpageRouter } from "./pages";
import { QuickShareButton } from "./share";
import type { CatalogResponse, PriceModel, SortKey } from "./types";
import {
  formatMoney,
  formatTokens,
  getInitialNumber,
  monthlyCost,
  sortModels,
} from "./utils";

const capabilities = ["reasoning", "tools", "vision", "structured", "openWeights"] as const;
const sortOptions: SortKey[] = ["monthly", "input", "output", "context", "newest"];

function readServerCatalog(): CatalogResponse | null {
  const element = document.getElementById("agentbuck-catalog");
  if (!element?.textContent) return null;
  try {
    const parsed = JSON.parse(element.textContent) as CatalogResponse;
    return parsed.models?.length ? parsed : null;
  } catch {
    return null;
  }
}

function ProviderLogo({ model, size = "md" }: { model: PriceModel; size?: "sm" | "md" | "lg" }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`provider-logo provider-logo--${size}`} aria-hidden="true">
      {failed ? (
        model.providerName.slice(0, 1)
      ) : (
        <img src={model.logo} alt="" onError={() => setFailed(true)} />
      )}
    </span>
  );
}

function FeatureDot({ active, label }: { active: boolean; label: string }) {
  return (
    <span className={`feature-dot ${active ? "is-active" : ""}`} title={label}>
      {active ? <Check size={12} strokeWidth={2.5} /> : "-"}
    </span>
  );
}

function ModelIdentity({ model, language }: { model: PriceModel; language: Language }) {
  return (
    <div className="model-identity">
      <ProviderLogo model={model} />
      <div className="model-copy">
        <a className="model-name-link" href={localizedPath(modelPath(model.providerId, model.slug), language)}>{model.name}</a>
        <a className="provider-name-link" href={localizedPath(providerPath(model.providerId), language)}>{model.providerName}</a>
      </div>
    </div>
  );
}

function App() {
  const params = new URLSearchParams(window.location.search);
  const route = useMemo(() => parseRoute(window.location.pathname, window.location.search), []);
  const serverCatalog = useMemo(readServerCatalog, []);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(serverCatalog);
  const [loading, setLoading] = useState(!serverCatalog);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [provider, setProvider] = useState(params.get("provider") ?? "all");
  const [sort, setSort] = useState<SortKey>((params.get("sort") as SortKey) ?? "monthly");
  const [activeCaps, setActiveCaps] = useState<string[]>([]);
  const [inputMillions, setInputMillions] = useState(() => getInitialNumber("input", 10));
  const [outputMillions, setOutputMillions] = useState(() => getInitialNumber("output", 2));
  const [cachePercent, setCachePercent] = useState(() => getInitialNumber("cache", 20));
  const [language] = useState<Language>(detectLanguage);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("agentbuck:favorites") ?? "[]");
    } catch {
      return [];
    }
  });
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [selected, setSelected] = useState<string[]>(route.type === "compare" ? route.modelUids : []);
  const [compareOpen, setCompareOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("agentbuck:theme");
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const t = (key: Parameters<typeof translate>[1], values?: Record<string, string | number>) =>
    translate(language, key, values);
  const locale = localeFor(language);
  const money = (value: number) => formatMoney(value, locale);

  const loadCatalog = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch("/api/catalog", { headers: { accept: "application/json" } });
      if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
      const data = (await response.json()) as CatalogResponse;
      if (!data.models?.length) throw new Error("Catalog is empty");
      setCatalog(data);
    } catch {
      setCatalog(fallbackCatalog);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!serverCatalog) void loadCatalog();
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("agentbuck:theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionFor(language);
    if (new URLSearchParams(window.location.search).get("lang") === language) saveLanguage(language);
  }, [language, locale]);

  useEffect(() => {
    localStorage.setItem("agentbuck:favorites", JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    // CalculatorPage owns its own query-string state (see src/pages.tsx) and rewrites the
    // search string itself; only keep `lang` in sync for it here.
    // CompareDirectoryPage owns no query state of its own -- it is grouped with the calculator
    // here so arbitrary query params on /compare (e.g. inbound attribution/tracking params) are
    // left untouched rather than normalized away, not because the page reads or writes any of them.
    const ownsQueryState = route.type === "calculator" || route.type === "compare-directory";
    const next = ownsQueryState ? new URLSearchParams(window.location.search) : new URLSearchParams();
    next.set("lang", language);
    if (!ownsQueryState) {
      if (route.type === "home") {
        if (query) next.set("q", query);
        if (provider !== "all") next.set("provider", provider);
        if (sort !== "monthly") next.set("sort", sort);
      }
      if (route.type === "compare") next.set("models", route.modelUids.join(","));
      if (route.type === "home" || route.type === "model" || route.type === "compare" || route.type === "curated-compare") {
        if (inputMillions !== 10) next.set("input", String(inputMillions));
        if (outputMillions !== 2) next.set("output", String(outputMillions));
        if (cachePercent !== 20) next.set("cache", String(cachePercent));
      }
    }
    const search = next.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`);
  }, [route, language, query, provider, sort, inputMillions, outputMillions, cachePercent]);

  const models = catalog?.models ?? [];
  const filteredModels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = models.filter((model) => {
      const matchesQuery =
        !normalizedQuery ||
        `${model.name} ${model.id} ${model.providerName} ${model.family ?? ""}`
          .toLowerCase()
          .includes(normalizedQuery);
      const matchesProvider = provider === "all" || model.providerId === provider;
      const matchesCaps = activeCaps.every(
        (capability) => model.capabilities[capability as keyof typeof model.capabilities],
      );
      const matchesFavorite = !favoritesOnly || favorites.includes(model.uid);
      return matchesQuery && matchesProvider && matchesCaps && matchesFavorite;
    });
    return sortModels(filtered, sort, inputMillions, outputMillions, cachePercent);
  }, [models, query, provider, activeCaps, favoritesOnly, favorites, sort, inputMillions, outputMillions, cachePercent]);

  const cheapestModels = useMemo(
    () =>
      sortModels(models, "monthly", inputMillions, outputMillions, cachePercent).slice(0, 5),
    [models, inputMillions, outputMillions, cachePercent],
  );

  const comparedModels = selected
    .map((uid) => models.find((model) => model.uid === uid))
    .filter((model): model is PriceModel => Boolean(model));
  const comparisonPath = localizedPath("/compare", language, {
    models: selected.join(","),
    input: inputMillions,
    output: outputMillions,
    cache: cachePercent,
  });

  const toggleFavorite = (uid: string) => {
    setFavorites((current) =>
      current.includes(uid) ? current.filter((item) => item !== uid) : [...current, uid],
    );
  };

  const toggleSelected = (uid: string) => {
    setSelected((current) => {
      if (current.includes(uid)) return current.filter((item) => item !== uid);
      if (current.length >= 3) return [...current.slice(1), uid];
      return [...current, uid];
    });
  };

  const toggleCapability = (key: string) => {
    setActiveCaps((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  const updateNumber = (setter: (value: number) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setter(Number.isFinite(value) && value >= 0 ? value : 0);
  };

  const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") setCompareOpen(false);
  };

  const switchLanguage = (nextLanguage: Language) => {
    saveLanguage(nextLanguage);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("lang", nextLanguage);
    window.location.assign(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href={localizedPath("/", language)} aria-label={t("aria.home")}>
          <span className="brand-mark"><BadgeDollarSign size={20} /></span>
          <span>AgentBuck</span>
        </a>
        <nav className="desktop-nav" aria-label={t("aria.navigation")}>
          <a href={localizedPath("/#prices", language)}>{t("nav.prices")}</a>
          <a href={localizedPath("/#calculator", language)}>{t("nav.calculator")}</a>
          <a href={localizedPath(CALCULATOR_PATH, language)}>{t("nav.requestCalculator")}</a>
          <a href={localizedPath(providerPath(), language)}>{t("nav.providers")}</a>
          <a href={localizedPath(GUIDE_PATH, language)}>{t("nav.guide")}</a>
          <a href={localizedPath("/rankings", language)}>{rankingsCopy(language)[0]}</a>
        </nav>
        <div className="topbar-actions">
          <label className="language-select" title={t("aria.language")}>
            <Languages size={16} />
            <select value={language} onChange={(event) => switchLanguage(event.target.value as Language)} aria-label={t("aria.language")}>
              {languages.map((code) => <option key={code} value={code} lang={code}>{languageNames[code]}</option>)}
            </select>
            <ChevronDown size={13} />
          </label>
          <QuickShareButton
            title={document.title}
            text={t("meta.description")}
            url={window.location.href}
            itemId={`page:${route.type}`}
            label={t("share.quick")}
          />
          <button
            className="icon-button"
            type="button"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            aria-label={theme === "light" ? t("aria.dark") : t("aria.light")}
            title={theme === "light" ? t("title.dark") : t("title.light")}
          >
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </header>

      {route.type === "home" ? <main>
        <section className="intro-band">
          <div className="intro-copy">
            <div className="eyebrow"><span className="live-dot" /> {t("hero.eyebrow")}</div>
            <h1>{t("hero.title")}</h1>
            <p>{t("hero.subtitle")}</p>
          </div>
          <dl className="catalog-stats">
            <div><dt>{t("stats.models")}</dt><dd>{loading ? "--" : models.length}</dd></div>
            <div><dt>{t("stats.providers")}</dt><dd>{loading ? "--" : catalog?.providers.length}</dd></div>
            <div>
              <dt>{t("stats.updated")}</dt>
              <dd>{catalog?.updatedAt.slice(0, 10) || t("common.loading")}</dd>
            </div>
          </dl>
        </section>

        {error && (
          <div className="notice" role="status">
            <Info size={16} />
            <span>{t("notice.snapshot")}</span>
            <button type="button" onClick={() => void loadCatalog()}><RotateCw size={14} /> {t("common.retry")}</button>
          </div>
        )}

        <section className="calculator-section" id="calculator">
          <div className="section-heading">
            <div>
              <span className="section-icon"><Calculator size={17} /></span>
              <h2>{t("calculator.title")}</h2>
            </div>
          </div>

          <div className="calculator-grid">
            <div className="usage-controls">
              <label>
                <span>{t("calculator.input")}</span>
                <div className="number-input">
                  <input type="number" min="0" step="1" value={inputMillions} onChange={updateNumber(setInputMillions)} />
                  <span>{t("calculator.million")}</span>
                </div>
              </label>
              <label>
                <span>{t("calculator.output")}</span>
                <div className="number-input">
                  <input type="number" min="0" step="1" value={outputMillions} onChange={updateNumber(setOutputMillions)} />
                  <span>{t("calculator.million")}</span>
                </div>
              </label>
              <label className="cache-control">
                <span>{t("calculator.cache")} <strong>{cachePercent}%</strong></span>
                <input type="range" min="0" max="100" step="5" value={cachePercent} onChange={updateNumber(setCachePercent)} />
              </label>
            </div>

            <div className="cost-chart" aria-label={t("calculator.cheapest")}>
              <div className="chart-header">
                <span><BarChart3 size={15} /> {t("calculator.cheapest")}</span>
                <span>{t("calculator.perMonth")}</span>
              </div>
              {loading
                ? Array.from({ length: 5 }).map((_, index) => <div className="chart-skeleton" key={index} />)
                : cheapestModels.map((model, index) => {
                    const cost = monthlyCost(model, inputMillions, outputMillions, cachePercent);
                    const maxCost = monthlyCost(cheapestModels.at(-1)!, inputMillions, outputMillions, cachePercent) || 1;
                    return (
                      <button className="chart-row" type="button" key={model.uid} onClick={() => setQuery(model.name)}>
                        <span className="chart-rank">{index + 1}</span>
                        <ProviderLogo model={model} size="sm" />
                        <span className="chart-model">{model.name}</span>
                        <span className="chart-track"><i style={{ width: `${Math.max(8, (cost / maxCost) * 100)}%` }} /></span>
                        <strong>{money(cost)}</strong>
                      </button>
                    );
                  })}
            </div>
          </div>
        </section>

        <section className="prices-section" id="prices">
          <div className="prices-title-row">
            <div>
              <h2>{t("prices.title")}</h2>
              <p>{t("prices.subtitle")}</p>
            </div>
            <button className="filter-mobile-button" type="button" onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}>
              <SlidersHorizontal size={16} /> {t("filter.open")}
            </button>
          </div>

          <div className={`filters ${mobileFiltersOpen ? "is-open" : ""}`}>
            <label className="search-field">
              <Search size={17} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("filter.search")} aria-label={t("filter.search")} />
              {query && <button type="button" onClick={() => setQuery("")} aria-label={t("filter.clearSearch")}><X size={15} /></button>}
            </label>
            <label className="select-field">
              <select value={provider} onChange={(event) => setProvider(event.target.value)} aria-label={t("filter.provider")}>
                <option value="all">{t("filter.allProviders")}</option>
                {catalog?.providers.map((item) => (
                  <option value={item.id} key={item.id}>{item.name} ({item.count})</option>
                ))}
              </select>
              <ChevronDown size={15} />
            </label>
            <label className="select-field">
              <ArrowDownUp size={15} />
              <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)} aria-label={t("filter.sort")}>
                {sortOptions.map((value) => <option value={value} key={value}>{t(`sort.${value}` as Parameters<typeof translate>[1])}</option>)}
              </select>
              <ChevronDown size={15} />
            </label>
            <button type="button" className={`favorite-filter ${favoritesOnly ? "is-active" : ""}`} onClick={() => setFavoritesOnly(!favoritesOnly)}>
              <Heart size={15} fill={favoritesOnly ? "currentColor" : "none"} /> {t("filter.favorite")}
            </button>
          </div>

          <div className="capability-filters" aria-label={t("filter.open")}>
            {capabilities.map((key) => (
              <button key={key} type="button" className={activeCaps.includes(key) ? "is-active" : ""} onClick={() => toggleCapability(key)}>
                {activeCaps.includes(key) && <Check size={13} />} {t(`cap.${key}` as Parameters<typeof translate>[1])}
              </button>
            ))}
          </div>

          <div className="results-meta">
            <span>{t("results.found", { count: filteredModels.length })}</span>
            <span><Info size={13} /> {t("results.source")}</span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="compare-cell">{t("column.compare")}</th>
                  <th>{t("column.model")}</th>
                  <th>{t("column.input")}</th>
                  <th>{t("column.cached")}</th>
                  <th>{t("column.output")}</th>
                  <th>{t("column.monthly")}</th>
                  <th>{t("column.context")}</th>
                  <th className="capabilities-head">{t("column.capabilities")}</th>
                  <th aria-label={t("column.actions")} />
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 7 }).map((_, index) => (
                      <tr key={index} className="loading-row"><td colSpan={9}><span /></td></tr>
                    ))
                  : filteredModels.map((model) => {
                      const isSelected = selected.includes(model.uid);
                      const isFavorite = favorites.includes(model.uid);
                      return (
                        <tr key={model.uid} className={isSelected ? "is-selected" : ""}>
                          <td className="compare-cell">
                            <button className={`check-button ${isSelected ? "is-active" : ""}`} type="button" onClick={() => toggleSelected(model.uid)} aria-label={isSelected ? t("action.removeCompare") : t("action.addCompare")}>
                              {isSelected && <Check size={13} />}
                            </button>
                          </td>
                          <td>
                            <ModelIdentity model={model} language={language} />
                            <a className="provider-doc-link" href={model.providerDoc} target="_blank" rel="noreferrer">
                              {t("action.officialDocs")} <ExternalLink size={11} />
                            </a>
                          </td>
                          <td className="price-cell">{money(model.cost.input)}</td>
                          <td className="price-cell muted-price">{model.cost.cacheRead === undefined ? "-" : money(model.cost.cacheRead)}</td>
                          <td className="price-cell">{money(model.cost.output)}</td>
                          <td><strong className="monthly-price">{money(monthlyCost(model, inputMillions, outputMillions, cachePercent))}</strong></td>
                          <td><span className="context-value">{formatTokens(model.context)}</span></td>
                          <td>
                            <div className="feature-dots">
                              <FeatureDot active={model.capabilities.reasoning} label={t("cap.reasoning")} />
                              <FeatureDot active={model.capabilities.tools} label={t("cap.tools")} />
                              <FeatureDot active={model.capabilities.vision} label={t("cap.vision")} />
                            </div>
                          </td>
                          <td>
                            <div className="row-actions">
                              <QuickShareButton
                                title={t("model.title", { name: model.name })}
                                text={language === "en" && model.description ? model.description : t("model.descriptionFallback")}
                                url={new URL(localizedPath(modelPath(model.providerId, model.slug), language), window.location.origin).toString()}
                                itemId={model.uid}
                                label={t("share.quick")}
                              />
                              <button className={`heart-button ${isFavorite ? "is-active" : ""}`} type="button" onClick={() => toggleFavorite(model.uid)} aria-label={isFavorite ? t("action.removeFavorite") : t("action.addFavorite")}>
                                <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          <div className="mobile-model-list">
            {!loading && filteredModels.map((model) => {
              const isSelected = selected.includes(model.uid);
              const isFavorite = favorites.includes(model.uid);
              return (
                <article className={`model-card ${isSelected ? "is-selected" : ""}`} key={model.uid}>
                  <div className="model-card-head">
                    <ModelIdentity model={model} language={language} />
                    <div className="row-actions">
                      <QuickShareButton title={t("model.title", { name: model.name })} text={language === "en" && model.description ? model.description : t("model.descriptionFallback")} url={new URL(localizedPath(modelPath(model.providerId, model.slug), language), window.location.origin).toString()} itemId={model.uid} label={t("share.quick")} />
                      <button className={`heart-button ${isFavorite ? "is-active" : ""}`} type="button" onClick={() => toggleFavorite(model.uid)} aria-label={isFavorite ? t("action.removeFavorite") : t("action.addFavorite")}>
                        <Heart size={17} fill={isFavorite ? "currentColor" : "none"} />
                      </button>
                    </div>
                  </div>
                  <div className="mobile-prices">
                    <div><span>{t("column.input")}</span><strong>{money(model.cost.input)}</strong></div>
                    <div><span>{t("column.output")}</span><strong>{money(model.cost.output)}</strong></div>
                    <div><span>{t("column.monthly")}</span><strong>{money(monthlyCost(model, inputMillions, outputMillions, cachePercent))}</strong></div>
                  </div>
                  <div className="model-card-foot">
                    <span>{formatTokens(model.context)} {t("context.suffix")}</span>
                    <button type="button" className={isSelected ? "is-active" : ""} onClick={() => toggleSelected(model.uid)}>
                      {isSelected ? <Check size={14} /> : null} {isSelected ? t("action.added") : t("action.addCompare")}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {!loading && filteredModels.length === 0 && (
            <div className="empty-state">
              <Search size={24} />
              <strong>{t("empty.title")}</strong>
              <span>{t("empty.description")}</span>
              <button type="button" onClick={() => { setQuery(""); setProvider("all"); setActiveCaps([]); setFavoritesOnly(false); }}>{t("empty.clear")}</button>
            </div>
          )}
        </section>

        <HomeDiscovery language={language} t={t} />

        <section className="method-section" id="method">
          <div>
            <span className="section-icon"><Sparkles size={17} /></span>
            <h2>{t("method.title")}</h2>
          </div>
          <div className="method-grid">
            <p><strong>{t("method.priceTitle")}</strong>{t("method.priceBody")}</p>
            <p><strong>{t("method.sourceTitle")}</strong>{t("method.sourceBefore")} {t("method.sourceAfter")}</p>
            <p><strong>{t("method.costTitle")}</strong>{t("method.costBody")}</p>
          </div>
        </section>

      </main> : <SubpageRouter
        route={route}
        catalog={catalog}
        loading={loading}
        language={language}
        locale={locale}
        t={t}
        usage={{ inputMillions, outputMillions, cachePercent, setInputMillions, setOutputMillions, setCachePercent }}
      />}

      <footer>
        <a className="brand brand--small" href="/"><BadgeDollarSign size={17} /> AgentBuck</a>
        <span>{t("footer.tagline")}</span>
        <span>{t("footer.updated", { date: catalog ? new Date(catalog.updatedAt).toLocaleString(locale) : "-" })}</span>
      </footer>

      {selected.length > 0 && !compareOpen && (
        <div className="compare-tray">
          <div className="compare-selection">
            <span>{t("compare.selected", { count: selected.length })}</span>
            {comparedModels.map((model) => (
              <button type="button" key={model.uid} onClick={() => toggleSelected(model.uid)} title={t("compare.remove", { name: model.name })}>
                <ProviderLogo model={model} size="sm" />
                <span>{model.name}</span>
                <X size={13} />
              </button>
            ))}
          </div>
          <button className="primary-button" type="button" disabled={selected.length < 2} onClick={() => setCompareOpen(true)}>
            {t("compare.button")}
          </button>
        </div>
      )}

      {compareOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setCompareOpen(false)}>
          <div className="compare-dialog" role="dialog" aria-modal="true" aria-labelledby="compare-title" onMouseDown={(event) => event.stopPropagation()} onKeyDown={onDialogKeyDown} tabIndex={-1}>
            <div className="dialog-header">
              <div><span>{t("compare.kicker")}</span><h2 id="compare-title">{t("compare.title")}</h2></div>
              <div className="dialog-header-actions">
                <a className="secondary-button" href={comparisonPath}>{t("share.openComparison")}</a>
                <QuickShareButton title={t("compare.pageTitle")} text={t("compare.pageBody")} url={new URL(comparisonPath, window.location.origin).toString()} itemId={`compare:${selected.join("+")}`} label={t("share.quick")} />
                <button className="icon-button" type="button" onClick={() => setCompareOpen(false)} aria-label={t("compare.close")}><X size={19} /></button>
              </div>
            </div>
            <div className="compare-grid" style={{ gridTemplateColumns: `140px repeat(${comparedModels.length}, minmax(190px, 1fr))` }}>
              <div className="compare-label" />
              {comparedModels.map((model) => <div className="compare-model-head" key={model.uid}><ProviderLogo model={model} size="lg" /><strong>{model.name}</strong><span>{model.providerName}</span></div>)}
              <div className="compare-label">{t("compare.input")}</div>
              {comparedModels.map((model) => <strong key={model.uid}>{money(model.cost.input)}</strong>)}
              <div className="compare-label">{t("compare.cache")}</div>
              {comparedModels.map((model) => <strong key={model.uid}>{model.cost.cacheRead === undefined ? "-" : money(model.cost.cacheRead)}</strong>)}
              <div className="compare-label">{t("compare.output")}</div>
              {comparedModels.map((model) => <strong key={model.uid}>{money(model.cost.output)}</strong>)}
              <div className="compare-label">{t("compare.monthly")}</div>
              {comparedModels.map((model) => <strong className="compare-cost" key={model.uid}>{money(monthlyCost(model, inputMillions, outputMillions, cachePercent))}</strong>)}
              <div className="compare-label">{t("compare.context")}</div>
              {comparedModels.map((model) => <span key={model.uid}>{formatTokens(model.context)}</span>)}
              <div className="compare-label">{t("compare.capabilities")}</div>
              {comparedModels.map((model) => <div className="feature-dots" key={model.uid}><FeatureDot active={model.capabilities.reasoning} label={t("cap.reasoning")} /><FeatureDot active={model.capabilities.tools} label={t("cap.tools")} /><FeatureDot active={model.capabilities.vision} label={t("cap.vision")} /></div>)}
              <div className="compare-label">{t("compare.docs")}</div>
              {comparedModels.map((model) => <a key={model.uid} href={model.providerDoc} target="_blank" rel="noreferrer">{t("compare.viewPrice")} <ExternalLink size={13} /></a>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
